import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component } from '@angular/core';
import { Router } from '@angular/router';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IDatasource,
  IGetRowsParams,
  RowClickedEvent,
} from 'ag-grid-community';
import {
  PaulAdminApiService,
  PaulCoachLesson,
  PaulCoachRun,
  PaulLessonStatus,
} from '../../services/paul-admin/paul-admin-api.service';

type CoachingView = 'runs' | 'lessons' | 'training';

@Component({
  selector: 'app-paul-coaching',
  templateUrl: './paul-coaching.component.html',
  styleUrls: ['./paul-coaching.component.scss'],
})
export class PaulCoachingComponent {
  activeView: CoachingView = 'runs';
  isDark = localStorage.getItem('paul_admin_theme') !== 'light';
  searchText = '';
  statusFilter = '';
  sourceFilter = '';
  evaluationFilter = '';
  errorMessage = '';
  totalRows = 0;
  private gridApi?: GridApi;

  readonly gridOptions: GridOptions = {
    rowModelType: 'infinite',
    cacheBlockSize: 50,
    maxBlocksInCache: 4,
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [25, 50, 100, 200],
    animateRows: false,
    rowHeight: 68,
    headerHeight: 48,
    suppressCellFocus: true,
    tooltipShowDelay: 250,
    defaultColDef: {
      sortable: false,
      resizable: true,
      filter: false,
      minWidth: 110,
      wrapHeaderText: true,
    },
    overlayLoadingTemplate: '<span class="ag-overlay-loading-center">Loading PAUL coaching data…</span>',
    overlayNoRowsTemplate: '<span class="ag-overlay-loading-center">No coaching records matched these filters.</span>',
  };

  readonly runColumns: ColDef<PaulCoachRun>[] = [
    { field: 'created_at', headerName: 'Created', width: 180, valueFormatter: params => this.formatDate(params.value) },
    { field: 'source', headerName: 'Source', width: 140, cellRenderer: (params: ICellRendererParams<PaulCoachRun>) => this.sourceRenderer(params) },
    { field: 'username', headerName: 'User / Runner', width: 150, valueFormatter: params => params.value || '—' },
    { field: 'prompt', headerName: 'Question / Evaluation Case', flex: 2.1, minWidth: 390, cellRenderer: (params: ICellRendererParams<PaulCoachRun>) => this.questionRenderer(params) },
    {
      headerName: 'Result',
      width: 125,
      valueGetter: params => params.data?.evaluation_status || params.data?.response_status || 'unknown',
      cellRenderer: (params: ICellRendererParams<PaulCoachRun>) => this.statusRenderer(params),
    },
    { field: 'model', headerName: 'Execution', width: 205, cellRenderer: (params: ICellRendererParams<PaulCoachRun>) => this.modelRenderer(params) },
    { field: 'trace_summary', headerName: 'Tools', width: 190, cellRenderer: (params: ICellRendererParams<PaulCoachRun>) => this.toolsRenderer(params) },
    { field: 'correction_status', headerName: 'Coaching', width: 145, cellRenderer: (params: ICellRendererParams<PaulCoachRun>) => this.coachingRenderer(params) },
    { headerName: '', width: 118, pinned: 'right', resizable: false, cellRenderer: (params: ICellRendererParams<PaulCoachRun>) => this.detailsRenderer(params) },
  ];

  readonly lessonColumns: ColDef<PaulCoachLesson>[] = [
    { field: 'created_at', headerName: 'Created', width: 180, valueFormatter: params => this.formatDate(params.value) },
    { field: 'username', headerName: 'Coach', width: 150, valueFormatter: params => params.value || '—' },
    { field: 'title', headerName: 'Lesson', flex: 1.6, minWidth: 340, cellRenderer: (params: ICellRendererParams<PaulCoachLesson>) => this.lessonRenderer(params) },
    { field: 'lesson_type', headerName: 'Type', width: 165, cellRenderer: (params: ICellRendererParams<PaulCoachLesson>) => this.simpleBadgeRenderer(params, 'blue') },
    { field: 'severity', headerName: 'Severity', width: 115, cellRenderer: (params: ICellRendererParams<PaulCoachLesson>) => this.statusRenderer(params) },
    { field: 'status', headerName: 'Decision', width: 125, cellRenderer: (params: ICellRendererParams<PaulCoachLesson>) => this.statusRenderer(params) },
    { field: 'lifecycle_status', headerName: 'Lifecycle', width: 145, cellRenderer: (params: ICellRendererParams<PaulCoachLesson>) => this.statusRenderer(params) },
    { field: 'verification_status', headerName: 'Verification', width: 140, cellRenderer: (params: ICellRendererParams<PaulCoachLesson>) => this.statusRenderer(params) },
    { field: 'source_of_truth', headerName: 'Source of truth', flex: .8, minWidth: 190, valueFormatter: params => params.value || '—' },
    {
      field: 'training_eligible',
      headerName: 'Training Ready',
      width: 140,
      cellRenderer: (params: ICellRendererParams<PaulCoachLesson>) => this.simpleBadgeRenderer(params, params.value ? 'green' : 'muted', params.value ? 'Ready' : 'Not ready'),
    },
    { headerName: '', width: 118, pinned: 'right', resizable: false, cellRenderer: (params: ICellRendererParams<PaulCoachLesson>) => this.detailsRenderer(params) },
  ];

  get columnDefs(): ColDef[] {
    return this.activeView === 'runs' ? this.runColumns : this.lessonColumns;
  }

  constructor(
    private readonly paulApi: PaulAdminApiService,
    private readonly router: Router,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    this.installDatasource();
  }

  onRowClicked(event: RowClickedEvent): void {
    const data = event.data as PaulCoachRun | PaulCoachLesson | undefined;
    if (!data) {
      return;
    }
    if (this.activeView === 'runs') {
      this.openRun((data as PaulCoachRun).coach_run_uuid);
      return;
    }
    const lesson = data as PaulCoachLesson;
    this.openRun(lesson.coach_run_uuid, lesson.lesson_uuid);
  }

  setView(view: CoachingView): void {
    if (this.activeView === view) {
      return;
    }
    this.activeView = view;
    this.statusFilter = '';
    this.sourceFilter = '';
    this.evaluationFilter = '';
    this.gridApi?.setGridOption('columnDefs', this.columnDefs);
    this.installDatasource();
  }

  applyFilters(): void {
    this.installDatasource();
  }

  clearFilters(): void {
    this.searchText = '';
    this.statusFilter = '';
    this.sourceFilter = '';
    this.evaluationFilter = '';
    this.applyFilters();
  }

  openRun(runUuid: string, lessonUuid?: string): void {
    if (!runUuid) {
      return;
    }
    void this.router.navigate(['/paul/coaching/runs', runUuid], {
      queryParams: lessonUuid ? { lesson: lessonUuid } : undefined,
    });
  }

  openChat(): void {
    void this.router.navigate(['/paul/chat']);
  }

  toggleTheme(): void {
    this.isDark = !this.isDark;
    localStorage.setItem('paul_admin_theme', this.isDark ? 'dark' : 'light');
  }

  formatDate(value: unknown): string {
    if (!value) {
      return '—';
    }
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  }

  private installDatasource(): void {
    if (!this.gridApi) {
      return;
    }
    this.errorMessage = '';
    this.gridApi.setGridOption('datasource', this.datasource());
  }

  private datasource(): IDatasource {
    return {
      getRows: (params: IGetRowsParams) => {
        const limit = Math.max(1, params.endRow - params.startRow);
        if (this.activeView === 'runs') {
          this.paulApi.listRuns({
            limit,
            offset: params.startRow,
            q: this.searchText.trim() || undefined,
            correction_status: this.statusFilter || undefined,
            source: (this.sourceFilter || undefined) as 'admin_chat' | 'evaluation' | undefined,
            evaluation_status: (this.evaluationFilter || undefined) as 'passed' | 'failed' | 'error' | undefined,
          }).subscribe({
            next: response => {
              this.totalRows = response.meta.total;
              params.successCallback(response.runs, response.meta.total);
              this.changeDetector.detectChanges();
            },
            error: error => {
              this.errorMessage = this.errorText(error, 'PAUL runs could not be loaded.');
              params.failCallback();
              this.changeDetector.detectChanges();
            },
          });
          return;
        }

        this.paulApi.listLessons({
          limit,
          offset: params.startRow,
          q: this.searchText.trim() || undefined,
          status: this.activeView === 'training' ? 'accepted' : (this.statusFilter as PaulLessonStatus || undefined),
          verification_status: this.activeView === 'training' ? 'verified' : undefined,
        }).subscribe({
          next: response => {
            this.totalRows = response.meta.total;
            params.successCallback(response.lessons, response.meta.total);
            this.changeDetector.detectChanges();
          },
          error: error => {
            this.errorMessage = this.errorText(error, 'PAUL lessons could not be loaded.');
            params.failCallback();
            this.changeDetector.detectChanges();
          },
        });
      },
    };
  }

  private sourceRenderer(params: ICellRendererParams<PaulCoachRun>): HTMLElement {
    const run = params.data;
    return this.badge(run?.source === 'evaluation' ? 'Evaluation' : 'Admin chat', run?.source === 'evaluation' ? 'purple' : 'blue');
  }

  private questionRenderer(params: ICellRendererParams<PaulCoachRun>): HTMLElement {
    const run = params.data;
    const wrapper = document.createElement('div');
    wrapper.className = 'grid-primary-cell';
    const title = document.createElement('strong');
    title.textContent = run?.prompt || '—';
    wrapper.appendChild(title);
    const meta = document.createElement('span');
    meta.textContent = run?.source === 'evaluation'
      ? [run.evaluation_category, run.evaluation_case_id].filter(Boolean).join(' · ')
      : (run?.answer ? this.truncate(run.answer, 115) : 'No answer was returned');
    wrapper.appendChild(meta);
    return wrapper;
  }

  private lessonRenderer(params: ICellRendererParams<PaulCoachLesson>): HTMLElement {
    const lesson = params.data;
    const wrapper = document.createElement('div');
    wrapper.className = 'grid-primary-cell';
    const title = document.createElement('strong');
    title.textContent = lesson?.title || '—';
    wrapper.appendChild(title);
    const meta = document.createElement('span');
    meta.textContent = this.truncate(lesson?.correction || lesson?.expected_behavior || 'No correction text', 120);
    wrapper.appendChild(meta);
    return wrapper;
  }

  private modelRenderer(params: ICellRendererParams<PaulCoachRun>): HTMLElement {
    const value = String(params.value || 'unknown');
    const wrapper = document.createElement('div');
    wrapper.className = 'execution-cell';
    const title = document.createElement('strong');
    if (value === 'paul-qwen36') {
      title.textContent = 'GX10 · Qwen3.6 35B';
    } else if (value === 'paul_api_fast_path') {
      title.textContent = 'LLM bypassed';
    } else {
      title.textContent = value;
    }
    wrapper.appendChild(title);
    const meta = document.createElement('span');
    meta.textContent = value === 'paul_api_fast_path' ? 'Historical rule-routed run' : value;
    wrapper.appendChild(meta);
    return wrapper;
  }

  private toolsRenderer(params: ICellRendererParams<PaulCoachRun>): HTMLElement {
    const trace = params.data?.trace_summary || {};
    const tools = Array.isArray(trace.tools) ? trace.tools : [];
    const wrapper = document.createElement('div');
    wrapper.className = 'tool-cell';
    const primary = document.createElement('strong');
    primary.textContent = tools.length ? tools.join(', ') : 'No tools';
    wrapper.appendChild(primary);
    const secondary = document.createElement('span');
    const warnings = Array.isArray(trace.warnings) ? trace.warnings.length : 0;
    secondary.textContent = `${trace.tool_count || tools.length} call${(trace.tool_count || tools.length) === 1 ? '' : 's'}${warnings ? ` · ${warnings} warning${warnings === 1 ? '' : 's'}` : ''}`;
    wrapper.appendChild(secondary);
    return wrapper;
  }

  private coachingRenderer(params: ICellRendererParams<PaulCoachRun>): HTMLElement {
    const run = params.data;
    const wrapper = document.createElement('div');
    wrapper.className = 'coaching-cell';
    wrapper.appendChild(this.badge(run?.correction_status || 'open', run?.correction_status === 'coached' ? 'green' : 'muted'));
    const count = document.createElement('span');
    count.textContent = `${run?.lesson_count || 0} lesson${run?.lesson_count === 1 ? '' : 's'} · ${run?.training_ready_count || 0} ready`;
    wrapper.appendChild(count);
    return wrapper;
  }

  private statusRenderer(params: ICellRendererParams): HTMLElement {
    const value = String(params.value || 'unknown').toLowerCase();
    const tone = ['passed', 'answered', 'accepted', 'verified', 'low'].includes(value)
      ? 'green'
      : ['failed', 'error', 'rejected', 'high'].includes(value)
        ? 'red'
        : ['pending', 'captured', 'medium', 'open', 'needs_review'].includes(value)
          ? 'amber'
          : ['resolved', 'training_ready'].includes(value)
            ? 'green'
          : 'muted';
    return this.badge(value, tone);
  }

  private simpleBadgeRenderer(params: ICellRendererParams, tone: string, label?: string): HTMLElement {
    return this.badge(label || String(params.value || '—'), tone);
  }

  private detailsRenderer(params: ICellRendererParams<any>): HTMLElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'grid-details-button';
    button.textContent = 'View details';
    button.addEventListener('click', event => {
      event.stopPropagation();
      const data = params.data;
      if (!data) {
        return;
      }
      if ('lesson_uuid' in data) {
        this.openRun(data.coach_run_uuid, data.lesson_uuid);
      } else {
        this.openRun(data.coach_run_uuid);
      }
    });
    return button;
  }

  private badge(label: string, tone: string): HTMLElement {
    const element = document.createElement('span');
    element.className = `grid-pill grid-pill--${tone}`;
    element.textContent = label.replace(/_/g, ' ');
    return element;
  }

  private truncate(value: string, length: number): string {
    return value.length > length ? `${value.slice(0, length - 1)}…` : value;
  }

  private errorText(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      const message = error.error?.message || error.error?.error || error.message;
      if (typeof message === 'string' && message.trim() !== '') {
        return message;
      }
    }
    return fallback;
  }
}
